import React, { useEffect, useState } from 'react';
import { OnboardingCompanyInput } from '../OnboardingCompanyInput';
import cx from 'classnames';
import styles from './index.scss';
import { H5, Button, Flex } from 'design-system-web';
import { ErrorMessage } from 'components';
import { useOnboardingCompanies } from 'lane-shared/contexts/SignUpContext/useOnboardingCompanies';
import { FeatureFlag } from 'constants-flags';
import { useFlag } from 'lane-shared/hooks';
import {
  SignUpUnsupportedLocationError,
  SignUpPrivateLocationError,
  SignUpFullAccessError,
} from 'activate-errors';
import useLocation from 'lane-shared/hooks/location/useLocation';
import { LocationAvailabilityEnum } from 'lane-shared/types/LocationType';
import { WhiteLabelType } from 'lane-shared/types/WhiteLabelType';
import { useTranslation } from 'react-i18next';
import { useOnboardingAnalytics } from 'lane-shared/hooks/analytics';
import { useOnboardingBuildings } from 'lane-shared/contexts/SignUpContext/useOnboardingBuildings';
import { useWorkplaceOnboardingEnabled } from 'lane-shared/hooks/onboarding/useWorkplaceOnboardingEnabled';
import { Channel } from 'lane-shared/types/ChannelType';
import { ActiveChannelTypeEnum } from 'constants-channel';
import { OnboardingCompleteData } from '../..';
import { CantFindCompany } from '../../../../components/CantFindCompany/CantFindCompany';
import { CompanySelectionError } from 'components/onboarding/components/CompanySelectionError';

type Props = {
  buildingFromContext: Channel | null;
  userName: string;
  whitelabel: WhiteLabelType;
  isLoading: boolean;
  onComplete: (data: OnboardingCompleteData) => void;
  onCantFindCompany: (building: Channel, fullName: string) => void;
  onBack: () => void;
  onClear: () => void;
  invitedChannel?: Channel;
  presumedEmailDomain?: string;
  company: Channel | null;
  parentCompany: Channel | null;
  setCompany: (company: Channel | null) => void;
  setParentCompany: (parentCompany: Channel | null) => void;
  companySelectionRequiredDuringSignup?: boolean;
};

const TRANSLATION_KEYS = {
  header: 'web.onboarding.building.header',
  button: 'shared.onboarding.building.createAccountButton',
  finishButton: 'shared.onboarding.finishButton',
  backButton: 'shared.onboarding.backButton',
  companySearchTitle: 'shared.onboarding.company.name.search.title',
  companyTitle: 'shared.onboarding.company.title'
};

export const OnboardingSelectCompany = ({
  company,
  parentCompany,
  setCompany,
  setParentCompany,
  userName,
  whitelabel,
  isLoading,
  onComplete,
  onCantFindCompany,
  onBack,
  onClear,
  invitedChannel,
  buildingFromContext,
  presumedEmailDomain,
  companySelectionRequiredDuringSignup = false,
}: Props) => {
  const companySelectionFFOn = useFlag(
    FeatureFlag.CompanySelectionRequiredDuringSignup,
    false
  );
  const companySelectionRequired =
    companySelectionRequiredDuringSignup && companySelectionFFOn;
  const [showCompanySelectionRequiredMessage, setShowCompanySelectionRequiredMessage] =
    useState(false);
  const { onboardingTracker } = useOnboardingAnalytics();
  const [parentCompanySearch, setParentCompanySearch] = useState('');
  const [companyLocationSearch, setCompanyLocationSearch] = useState('');
  const [buildingFromState, setBuilding] = useState<Channel | null>(
    buildingFromContext
  );

  const [selectionError, setSelectionError] = useState<Error | null>(null);
  const { locationHelper } = useLocation();

  const { t } = useTranslation();

  const isWorkplaceOnboardingEnabled = useWorkplaceOnboardingEnabled();

  const building =
    isWorkplaceOnboardingEnabled && buildingFromContext
      ? buildingFromContext
      : buildingFromState;

  const isInvitedChannelCompany =
    invitedChannel?.type === ActiveChannelTypeEnum.Company;
  const isInvitedChannelBuilding =
    invitedChannel?.type === ActiveChannelTypeEnum.Property;

  const invitedBuildingId = isInvitedChannelBuilding
    ? invitedChannel?._id
    : null;
  const buildingId = isWorkplaceOnboardingEnabled
    ? building?._id
    : invitedBuildingId;

  const onboardingCompaniesResult = useOnboardingCompanies({
    query: parentCompanySearch,
    buildingId,
    presumedEmailDomain,
  });

  const onboardingBuildingsResult = useOnboardingBuildings({
    query: companyLocationSearch,
    channelId: parentCompany ? parentCompany?._id : null,
    skip:
      (!parentCompany?._id && !building?._id) || isWorkplaceOnboardingEnabled,
    presumedEmailDomain,
  });

  if (
    invitedChannel &&
    parentCompany !== invitedChannel &&
    isInvitedChannelCompany
  ) {
    setParentCompany(invitedChannel);
  }

  useEffect(() => {
    locationHelper.startWatching({
      showRequestModal: true,
      availability: LocationAvailabilityEnum.Foreground,
      disableLocationPrecision: whitelabel.disableLocationPrecision,
    });
  }, []);

  useEffect(() => {
    if (
      !parentCompany ||
      !onboardingBuildingsResult.called ||
      onboardingBuildingsResult.isInitialLoading
    ) {
      return;
    }

    const locations = onboardingBuildingsResult.buildings;

    if (locations.length === 0) {
      setSelectionError(new SignUpUnsupportedLocationError());
    } else if (locations.every(l => l.node.inviteOnly)) {
      setSelectionError(new SignUpPrivateLocationError());
    } else if (parentCompany.inviteOnly) {
      setSelectionError(new SignUpFullAccessError());
    } else {
      setSelectionError(null);
    }
  }, [
    parentCompany,
    onboardingBuildingsResult.called,
    onboardingBuildingsResult.isInitialLoading,
    onboardingBuildingsResult.buildings.length,
  ]);

  const handleCompanyLocationSearchTextChange = (
    companyLocationSearch: string
  ) => setCompanyLocationSearch(companyLocationSearch);

  const clearSelectionError = () => {
    setSelectionError(null);
    onClear();
  };

  const handleCompanyBuildingSelectionChange = (
    selectedCompany: Channel | null,
    selectedBuilding: Channel | null
  ) => {
    setCompany(selectedCompany);
    setBuilding(selectedBuilding);

    if (selectedBuilding) {
      onboardingTracker.Select.Building({
        buildingId: selectedBuilding?._id,
      });
    }

    if (selectedCompany === null && selectedBuilding === null) {
      clearSelectionError();
      onboardingTracker.Remove.Building({ buildingId: building?._id! });
    }
  };

  const handleParentCompanySearchTextChange = (parentCompanySearch: string) =>
    setParentCompanySearch(parentCompanySearch);

  const handleParentCompanySelectionChange = (parentCompany: Channel) => {
    onboardingTracker.Select.Company({ companyId: parentCompany?._id! });
    setParentCompany(parentCompany);
    setShowCompanySelectionRequiredMessage(false);

    if (isInvitedChannelBuilding) {
      handleCompanyBuildingSelectionChange(parentCompany, invitedChannel!);
    }
  };

  const handleSelectionReset = () => {
    setParentCompany(null);
    setCompany(null);
    setBuilding(null);
    setShowCompanySelectionRequiredMessage(false);
    clearSelectionError();
    onboardingTracker.Remove.Company({ companyId: parentCompany?._id! });
    onboardingTracker.Remove.Building({ buildingId: building?._id! });
  };

  const handleCreateAccount = () => {
    if (company && building) {
      if (isWorkplaceOnboardingEnabled) {
        handleCompanyBuildingSelectionChange(company, building);
      }

      onComplete({ company, building, userName });
    }
  };

  const handleCantFindCompany = () => {
    if (companySelectionRequired && !company?._id) {
      setShowCompanySelectionRequiredMessage(true);
    } else if (building) {
      onCantFindCompany(building, userName);
    }
  };

  const noEligibleCompanies =
    building &&
    !onboardingCompaniesResult.isInitialLoading &&
    onboardingCompaniesResult.companies.length === 0;

  const isFinishDisabled = companySelectionRequired
    ? noEligibleCompanies || !company?._id
    : !company?._id;

  const showCompanySelectionRequiredNoCompanies =
    companySelectionRequired && noEligibleCompanies;
  const showCompanyRequiredError =
    showCompanySelectionRequiredMessage || showCompanySelectionRequiredNoCompanies;

  return (
    <Flex direction="column" align="center" className={styles.flexContainer}>
      <H5 mb={2} className={styles.title}>
        {parentCompany && !isWorkplaceOnboardingEnabled
          ? t(TRANSLATION_KEYS.companyTitle)
          : t(TRANSLATION_KEYS.companySearchTitle)}
      </H5>
      <div className={styles.searchContainer}>
        {selectionError && (
          <ErrorMessage
            className={styles.searchErrorContainer}
            alertClassName={styles.searchError}
            error={selectionError.message}
          />
        )}
        {!selectionError && showCompanyRequiredError && (
          <CompanySelectionError
            whitelabelName={whitelabel?.name}
            supportEmail={whitelabel?.profile?.email}
          />
        )}
        <OnboardingCompanyInput
          data={{
            signupError: selectionError,
            building,
            parentCompanySearch,
            onboardingCompaniesResult,
            companyLocationSearch,
            parentCompany,
            onboardingBuildingsResult,
          }}
          handlers={{
            resetSelection: handleSelectionReset,
            onParentCompanySearchTextChange:
              handleParentCompanySearchTextChange,
            onParentCompanySelectionChange: handleParentCompanySelectionChange,
            onCompanyLocationSearchTextChange:
              handleCompanyLocationSearchTextChange,
            onCompanyBuildingSelectionChange:
              handleCompanyBuildingSelectionChange,
          }}
        />
      </div>
      {!isWorkplaceOnboardingEnabled && company && (
        <Button
          loading={isLoading}
          className={styles.button}
          fullWidth
          onClick={handleCreateAccount}
          size="large"
          type="submit"
          disabled={
            !building?._id && Boolean(onboardingBuildingsResult.isSignUpBlocked)
          }
        >
          {t(TRANSLATION_KEYS.button)}
        </Button>
      )}
      {isWorkplaceOnboardingEnabled && (
        <>
          <Button
            loading={isLoading}
            className={styles.button}
            fullWidth
            onClick={handleCreateAccount}
            size="large"
            type="submit"
            disabled={isFinishDisabled}
          >
            {t(TRANSLATION_KEYS.finishButton)}
          </Button>
          {!isInvitedChannelBuilding && (
            <Button
              loading={isLoading}
              className={cx(styles.button, styles.buttonWorkplaceOnboarding)}
              fullWidth
              onClick={onBack}
              size="large"
              variant="secondary"
            >
              {t(TRANSLATION_KEYS.backButton)}
            </Button>
          )}
        </>
      )}
      <CantFindCompany
        parentCompany={parentCompany}
        invitedChannelId={invitedChannel?._id ?? null}
        onClick={handleCantFindCompany}
      />
    </Flex>
  );
};
